import { useState } from 'react';

export interface TotpRecoveryCodesProps {
  codes: string[];
  onContinue: () => void;
}

export default function TotpRecoveryCodes({
  codes,
  onContinue,
}: TotpRecoveryCodesProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  function downloadTxt() {
    const blob = new Blob(
      [
        'Avocado Solana — Recovery Codes\n',
        `Generated: ${new Date().toISOString()}\n\n`,
        'Each code can be used ONCE to approve a transaction or remove the backend signer if you lose your TOTP device.\n',
        'Store them somewhere safe — anyone with these codes can sign on your behalf with TOTP equivalent privileges.\n\n',
        ...codes.map((c, i) => `${i + 1}. ${c}\n`),
      ],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'avocado-solana-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function copyAll() {
    await navigator.clipboard.writeText(codes.join('\n'));
  }

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200">
        <strong className="block mb-1">Save these codes somewhere safe.</strong>
        They&rsquo;re shown <em>once</em>. Each code is single-use. If you lose
        your authenticator and these codes, you cannot recover funds.
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-xl border border-ink-800 bg-ink-900/60 p-4 font-mono text-sm">
        {codes.map((c, i) => (
          <div key={i} className="rounded bg-ink-950/60 px-3 py-2">
            <span className="text-ink-400 mr-2">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="text-ink-50">{c}</span>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={downloadTxt}
          className="flex-1 rounded-lg border border-ink-800 bg-ink-900/60 px-4 py-2.5 text-sm text-ink-100 hover:bg-ink-800/60 transition-colors"
        >
          Download .txt
        </button>
        <button
          type="button"
          onClick={() => void copyAll()}
          className="flex-1 rounded-lg border border-ink-800 bg-ink-900/60 px-4 py-2.5 text-sm text-ink-100 hover:bg-ink-800/60 transition-colors"
        >
          Copy all
        </button>
      </div>

      <label className="flex items-start gap-3 text-sm text-ink-200 cursor-pointer">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="mt-0.5 accent-avo-500"
        />
        <span>I&rsquo;ve saved these codes in a secure place.</span>
      </label>

      <button
        type="button"
        disabled={!acknowledged}
        onClick={onContinue}
        className="w-full rounded-lg bg-avo-500 px-4 py-3 text-sm font-medium text-ink-950 hover:bg-avo-400 transition-colors disabled:opacity-50"
      >
        Continue
      </button>
    </div>
  );
}
